import { memo, useCallback, useEffect } from "react";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { NumberField } from "ui/components/TextField/NumberField";
import { DatePicker } from "ui/components/DatePicker";
import { InvoiceStatusDropdown } from "ui/components/Dropdown/InvoiceStatusDropdown";
import { useTranslate } from "helpers";
import { InvoiceSearchFilterProps } from "./types";
import { InvoiceOutgoingSearchFormValues } from "services/api/fleet/vehicles/invoices/types";
import { initialFleetVehicleInvoiceSearchForm } from "services/api/fleet/vehicles/invoices/consts";
import {
  useHandleTextFieldChange,
  useHandleDropdownChange,
  useHandleDateChange,
  useHandleNumberFieldChange,
} from "services/forms/hooks";
import useUrlFilters from "services/hooks/useUrlFilters";


const InvoiceSearchFilters: React.FC<
  InvoiceSearchFilterProps
> = (props) => {
  const { onAddNew, onSearchChange } = props;

  const { t } = useTranslate();

  // Status + datumski raspon (Račun od/do) sinkronizirani s URL-om — drill-down s dashboarda + share/reload.
  const [formValues, setFormValues] = useUrlFilters<InvoiceOutgoingSearchFormValues>(
    initialFleetVehicleInvoiceSearchForm,
    {
      invoiceStatusId: { param: "status" },
      invoiceDateFrom: { param: "invoiceDateFrom", type: "date" },
      invoiceDateTo: { param: "invoiceDateTo", type: "date-end" },
    }
  );

  useEffect(() => {
    onSearchChange(formValues);
  }, [formValues]);

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);
  const handleNumberFieldChange = useHandleNumberFieldChange(setFormValues, formValues);

  const handleResetFormClick = useCallback(() => {
    setFormValues(initialFleetVehicleInvoiceSearchForm);
  }, []);

  return (
    <div className="flex flex-wrap items-end gap-2 mb-3">
      <div className="w-[150px]">
        <TextField
          type="text"
          name="search"
          value={formValues.search}
          onChange={handleTextFieldChange}
          placeholder="Pretraži..."
          startIcon="/media/icons/search.svg"
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[150px]">
        <InvoiceStatusDropdown
          name="invoiceStatusId"
          value={formValues.invoiceStatusId}
          onChange={handleDropdownChange}
          placeholder="Status"
          width="full"
        />
      </div>
      <div className="w-[120px]">
        <DatePicker
          name="invoiceDateFrom"
          label="Račun od - do"
          value={formValues.invoiceDateFrom}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[120px]">
        <DatePicker
          name="invoiceDateTo"
          value={formValues.invoiceDateTo}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[120px]">
        <DatePicker
          name="dueDateFrom"
          label="Dospijeće od - do"
          value={formValues.dueDateFrom}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[120px]">
        <DatePicker
          name="dueDateTo"
          value={formValues.dueDateTo}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[90px]">
        <NumberField
          name="totalMin"
          label="Iznos od - do"
          placeholder="Iznos od"
          value={formValues.totalMin}
          onChange={handleNumberFieldChange}
          decimals={2}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[90px]">
        <NumberField
          name="totalMax"
          placeholder="do"
          value={formValues.totalMax}
          onChange={handleNumberFieldChange}
          decimals={2}
          cssClassName="bg-white"
        />
      </div>
      <span
        onClick={handleResetFormClick}
        className="text-xs font-bold cursor-pointer text-gray-500 hover:text-sky-600 pb-2 whitespace-nowrap"
      >
        RESET
      </span>
      <div className="ml-auto">
        <Button
          variant="primary"
          children={t("admin.vehicle.invoice.sale.search.add")}
          startIcon="/media/icons/plus.svg"
          onClick={onAddNew}
        />
      </div>
    </div>
  );
};
export default memo(InvoiceSearchFilters);
